import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// Three key lights on the line to the sea: bright midday, golden late
// afternoon, and sunset over the terminus.
const STOPS = {
  skyTop: ['#4E9FE4', '#6F9FD6', '#3A4688'],
  skyMiddle: ['#93C9EE', '#C9D3C8', '#C77F9A'],
  skyHorizon: ['#DCEFF4', '#FFDFA0', '#FFAE72'],
  sun: ['#FFFDF2', '#FFF0C4', '#FFC27A'],
  sunGlow: ['#FFF8DC', '#FFD98E', '#FF9858'],
  cloudLit: ['#FFFFFF', '#FFF3DA', '#FFD2A8'],
  cloudShade: ['#B7CBE8', '#D8BCB8', '#A47FA4'],
  mountainBack: ['#B3CBE8', '#C9C2D8', '#B08EAE'],
  mountain: ['#86A8D6', '#A1A0C6', '#8C74A6'],
  mountainShade: ['#6E8EC2', '#8382B2', '#6E5A8E'],
  snow: ['#F2F7FF', '#FFF0DC', '#FFD2BC'],
  hillFar: ['#93C2A6', '#B3BE8E', '#AA8E92'],
  hillFarShade: ['#7AAE98', '#97A27E', '#8A7084'],
  hill: ['#8DC75E', '#B5C556', '#BFA852'],
  hillShade: ['#55965A', '#71904A', '#5E6656'],
  wheat: ['#EAD77E', '#F6CA62', '#EAA662'],
  meadow: ['#AAD66C', '#CBD166', '#D6B25E'],
  grass: ['#7CBF4C', '#9DBB46', '#A49A44'],
  grassShade: ['#3E7C3C', '#4F7034', '#4A5236'],
  foliage: ['#4C9846', '#68923E', '#6E7A40'],
  foliageLit: ['#94D05E', '#CDCB5C', '#E2A85E'],
  foliageShade: ['#2C643A', '#3A5834', '#363A3E'],
  trunk: ['#5C4834', '#5E4430', '#41302E'],
  stone: ['#D4CBB8', '#E2C9A0', '#D2A48E'],
  stoneShade: ['#958C7C', '#A0846A', '#7E5E62'],
  brick: ['#B65E46', '#C2643E', '#A44C4A'],
  roof: ['#B4483A', '#BE4C34', '#94384A'],
  wall: ['#F6F0E2', '#FFEBCB', '#F4C8B2'],
  water: ['#8ACCEC', '#B8CDD0', '#E8A28E'],
  waterDeep: ['#4A8EC0', '#5C88A8', '#6A5E8C'],
  sea: ['#58A6D2', '#6B9CC0', '#8A82B6'],
  seaDeep: ['#2E709F', '#3C6A94', '#3C4A84'],
  sand: ['#F4E0AE', '#F7CF90', '#EAAC8C'],
  haze: ['#D6ECF4', '#FCE0AC', '#F7B48E'],
  rim: ['#FFFFFF', '#FFE6A2', '#FFC48A'],
  wash: ['#FFFFFF', '#FFD596', '#FF9C7E'],
  ink: ['#2C3E50', '#3A3A50', '#35294A'],
} as const;

type ColorName = keyof typeof STOPS;

export type RailwayColors = Record<ColorName, Rgb>;

/** Number of key lights; the palette blends between neighbouring keys. */
export const RAILWAY_KEYS = 3;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = {} as Record<ColorName, Rgb[]>;
NAMES.forEach((name) => {
  PARSED[name] = STOPS[name].map(hexToRgb);
});
// Each key light's palette, built once and shared (read-only).
const KEY_COLORS = Array.from({ length: RAILWAY_KEYS }, (_, key) => {
  const colors = {} as RailwayColors;
  NAMES.forEach((name) => {
    colors[name] = PARSED[name][key];
  });
  return colors;
});

/** The palette exactly at key light `index` (0 midday, 1 golden afternoon, 2 sunset). */
export const railwayKeyColors = (index: number): RailwayColors =>
  KEY_COLORS[Math.min(RAILWAY_KEYS - 1, Math.max(0, Math.round(index) || 0))];

/** The pair of key lights a light amount (0..1) falls between, and the eased blend toward the second. */
export const railwayKeyBlend = (light: number) => {
  const scaled = clamp(light) * (RAILWAY_KEYS - 1);
  const index = Math.min(RAILWAY_KEYS - 2, Math.floor(scaled));
  return { index, amount: smoothstep(0, 1, scaled - index) };
};

/**
 * The light along the line: the journey runs from midday into the golden
 * afternoon, and arriving at the sea brings the sunset. The scene hands in an
 * eased `finale` (see railwayDuskFinale) so the sunset never rushes.
 */
export const railwayLight = (progress: number, finale: number) =>
  clamp((1 - Math.pow(1 - clamp(progress), 1.6)) * 0.5 + clamp(finale) * 0.5);

/**
 * How far into the sunset key light a finale amount takes the palette (the
 * golden-to-sunset blend at the end of the line).
 */
export const railwayDuskAmount = (finale: number) => smoothstep(0, 1, clamp(finale));

/**
 * The finale amount whose sunset blend is `amount`: the inverse of
 * railwayDuskAmount, so the scene can pace the sunset evenly in colour.
 */
export const railwayDuskFinale = (amount: number) => 0.5 - Math.sin(Math.asin(clamp(1 - 2 * amount, -1, 1)) / 3);

/** The palette at a light amount (0 midday … 1 sunset). */
export const railwayColors = (light: number): RailwayColors => {
  const { index, amount } = railwayKeyBlend(light);
  const colors = {} as RailwayColors;
  for (let n = 0; n < NAMES.length; n += 1) {
    const name = NAMES[n];
    colors[name] = mixRgb(PARSED[name][index], PARSED[name][index + 1], amount);
  }
  return colors;
};

/** Dark header text under the blue and golden skies; white once the sunset deepens the top of the sky. */
export const railwayChromeTone = (progress: number): ChromeTone => {
  const light = progress >= 0.995 ? 1 : railwayLight(progress, 0);
  return luminance(railwayColors(light).skyTop) > 0.2 ? 'dark' : 'light';
};
